import { useEffect, useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Search, ShoppingBag, Menu, X } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { LogoWordmark } from "./Logo";
import { useShop } from "@/context/ShopContext";

const NAV = [
  { to: "/", label: "Home" },
  { to: "/shop", label: "Shop" },
  { to: "/about", label: "About" },
  { to: "/reviews", label: "Reviews" },
  { to: "/contact", label: "Contact" },
];

export default function Header() {
  const { cartCount, setCartOpen, setSearchOpen } = useShop();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <>
      <header
        data-testid="site-header"
        className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 border-b ${
          scrolled
            ? "bg-[#080808]/90 backdrop-blur-xl border-[#D4AF37]/15"
            : "bg-[#080808]/60 backdrop-blur-md border-transparent"
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 h-20 flex items-center justify-between">
          <button
            className="lg:hidden text-white/90 hover:text-[#D4AF37] transition-colors"
            onClick={() => setMobileOpen(true)}
            aria-label="Open menu"
            data-testid="mobile-menu-open-button"
          >
            <Menu size={22} strokeWidth={1.5} />
          </button>

          <nav className="hidden lg:flex items-center gap-8 flex-1" data-testid="desktop-nav">
            {NAV.slice(0, 3).map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`nav-${l.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `font-sans-body text-[11px] uppercase tracking-[0.25em] transition-colors duration-300 hover:text-[#D4AF37] ${
                    isActive ? "text-[#D4AF37]" : "text-white/80"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
          </nav>

          <Link to="/" className="flex-shrink-0" data-testid="header-logo-link">
            <LogoWordmark />
          </Link>

          <div className="flex items-center gap-6 flex-1 justify-end">
            <nav className="hidden lg:flex items-center gap-8" data-testid="desktop-nav-right">
              {NAV.slice(3).map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  data-testid={`nav-${l.label.toLowerCase()}`}
                  className={({ isActive }) =>
                    `font-sans-body text-[11px] uppercase tracking-[0.25em] transition-colors duration-300 hover:text-[#D4AF37] ${
                      isActive ? "text-[#D4AF37]" : "text-white/80"
                    }`
                  }
                >
                  {l.label}
                </NavLink>
              ))}
            </nav>
            <button
              onClick={() => setSearchOpen(true)}
              className="text-white/90 hover:text-[#D4AF37] transition-colors"
              aria-label="Search"
              data-testid="search-open-button"
            >
              <Search size={20} strokeWidth={1.5} />
            </button>
            <button
              onClick={() => setCartOpen(true)}
              className="relative text-white/90 hover:text-[#D4AF37] transition-colors"
              aria-label="Open cart"
              data-testid="cart-open-button"
            >
              <ShoppingBag size={20} strokeWidth={1.5} />
              {cartCount > 0 && (
                <span
                  className="absolute -top-2 -right-2 bg-[#D4AF37] text-[#080808] text-[10px] font-sans-body font-bold w-4.5 h-4.5 min-w-[18px] min-h-[18px] rounded-full flex items-center justify-center"
                  data-testid="cart-count-badge"
                >
                  {cartCount}
                </span>
              )}
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            className="fixed inset-0 z-[60] bg-[#080808]/97 backdrop-blur-xl flex flex-col"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            data-testid="mobile-menu"
          >
            <div className="flex items-center justify-between px-6 h-20 border-b border-[#D4AF37]/15">
              <LogoWordmark compact />
              <button onClick={() => setMobileOpen(false)} aria-label="Close menu" data-testid="mobile-menu-close-button"
                className="text-white/90 hover:text-[#D4AF37] transition-colors">
                <X size={24} strokeWidth={1.5} />
              </button>
            </div>
            <nav className="flex flex-col items-center justify-center flex-1 gap-8">
              {NAV.map((l, i) => (
                <motion.div key={l.to} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.08 * i }}>
                  <button
                    onClick={() => { setMobileOpen(false); navigate(l.to); }}
                    data-testid={`mobile-nav-${l.label.toLowerCase()}`}
                    className="font-serif-display text-3xl text-white/90 hover:text-[#D4AF37] transition-colors"
                  >
                    {l.label}
                  </button>
                </motion.div>
              ))}
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
